// The template editor's single form: its values, and the three mappings that
// hang off them — from a saved template, to a PATCH body, and to a preview
// draft. Kept out of the components so the rules stay readable on their own.
//
// Sections round-trip as a plain list — `toSectionValues` in, `toWireSection`
// out — one section at a time, in whatever order section-list.tsx leaves
// them. An emptied per-section field (an image's asset, a metric's unit)
// goes out as an explicit null, the same clear-on-empty rule every other
// field on this form follows. The list itself is never defaulted on the way
// out: an operator who removes every section is left with an empty list,
// which previews (and serves) as background-only rather than snapping back
// to the product's starter content.

import { z } from "zod";
import type { Template, TemplateBody, TemplateSection } from "@/api/types";
import type { PreviewTemplate } from "@/features/preview/preview-panel";
import { isPristineCss, TEMPLATE_CSS_SCAFFOLD } from "@/features/theming/css-scaffold";

const HEX = /^#[0-9a-fA-F]{6}$/;

// Curated font stacks (label -> full CSS stack). "custom" reveals free text.
// The families the display page bundles and serves itself — see
// frontend/display/src/fonts.css, which is the source of truth. Anything else
// depends on the signage box having a typeface installed, and these boxes run a
// Chrome old enough that we cannot assume much; tests/font-parity.test.ts fails
// if this list and the display's @font-face definitions drift apart.
//
// The fallback behind each is for the window between first paint and font load,
// not a substitute: if the woff2 fails to fetch, the sign still renders.
// Alphabetical by label: the picker renders in insertion order, and a list of
// typefaces has no meaningful order other than the one someone can scan.
export const FONT_STACKS: Record<string, string> = {
    "google-sans": '"Google Sans", system-ui, sans-serif',
    inter: "Inter, system-ui, sans-serif",
    "roboto-mono": '"Roboto Mono", ui-monospace, monospace',
};

// Every template names its typeface. There is deliberately no "display
// default" row: it stored a null stack, which the display page substitutes its
// own default for — and now that the default is the bundled Inter, that row
// rendered identically to picking Inter while implying something else was
// happening. `DEFAULT_FONT_STACK` in the display still matters, but for the case
// no picker can cover: a screen with no template at all.
export const FONT_LABELS: Record<string, string> = {
    // Families alphabetically; the cards render in this order.
    "google-sans": "Google Sans",
    inter: "Inter",
    "roboto-mono": "Roboto Mono",
};

/** The fourth choice: a font file from the workspace's asset library. */
export const OWN_FONT = "own";

/** What signage shows while an uploaded font loads, or if it fails. */
export const UPLOADED_FONT_FALLBACK = FONT_STACKS.inter as string;

export function fontChoiceFor(stack: string | null, fontAssetId: string | null = null): string {
    if (fontAssetId) return OWN_FONT;
    // A null stack renders the display's default, Inter, and so does any stack
    // the cards no longer offer (free text, or a family since dropped) once the
    // template is saved again. Saving writes the stack explicitly, which changes
    // the stored value but not a single rendered pixel for a null one.
    const match = Object.entries(FONT_STACKS).find(([, value]) => value === stack);
    return match ? match[0] : "inter";
}

const hexOrEmpty = z.union([z.literal(""), z.string().regex(HEX, "Use #rrggbb")]);

/** A section in form state. `assetId` is "" rather than null while it is in a
 *  picker — the same empty-string convention every other artwork slot uses —
 *  and `toBody` converts on the way out. */
/** A block's size, as a percent of its default. */
export const DEFAULT_SECTION_SIZE = 100;
const sizeSchema = z.number().int().min(50).max(200);

export const sectionSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("text"), id: z.string(), text: z.string(), size: sizeSchema }),
    z.object({ type: z.literal("image"), id: z.string(), assetId: z.string() }),
    z.object({ type: z.literal("metric"), id: z.string(), unit: z.string(), size: sizeSchema }),
]);

/** Omitted at the default, so an untouched template saves exactly as before
 *  sizes existed. */
function wireSize(size: number): { size?: number } {
    return size === DEFAULT_SECTION_SIZE ? {} : { size };
}

export type SectionValues = z.infer<typeof sectionSchema>;

/** The first unused id — s1, s2, s3, … — so deleting the middle of a list and
 *  adding again reuses the gap rather than climbing forever. Minted here and
 *  never shown as an editable field: it is what template CSS targets through
 *  [data-section="…"], and an operator renaming it would silently break their
 *  own stylesheet. */
export function nextSectionId(existing: readonly { id: string }[]): string {
    const used = new Set(existing.map((section) => section.id));
    for (let n = 1; ; n++) {
        const candidate = `s${n}`;
        if (!used.has(candidate)) return candidate;
    }
}

export const templateSchema = z
    .object({
        name: z.string().trim().min(1, "Name is required"),
        backgroundColor: z.string().regex(HEX, "Use #rrggbb"),
        // "" is "no image": the picker's own empty state, and what `toBody` omits.
        backgroundImageAssetId: z.string(),
        layout: z.enum(["column", "row"]),
        distribution: z.enum(["center", "spread", "start", "end"]),
        sections: z.array(sectionSchema),
        fontChoice: z.string(),
        // "" is "no font file": the picker's own empty state, like every asset slot.
        fontAssetId: z.string(),
        slotText: hexOrEmpty,
        customCss: z.string(),
        previewWidth: z.number().int().positive(),
        previewHeight: z.number().int().positive(),
    })
    .superRefine((values, ctx) => {
        if (values.fontChoice === OWN_FONT && !values.fontAssetId) {
            ctx.addIssue({
                code: "custom",
                path: ["fontAssetId"],
                message: "Upload a font or choose one from the library",
            });
        }
    });

export type TemplateValues = z.infer<typeof templateSchema>;

/** A blank template, for the create page. Matches the display page's own
 *  fallbacks so the preview opens on what an unstyled screen already looks
 *  like, rather than on something the product never shows. */
export const NEW_TEMPLATE_DEFAULTS: TemplateValues = {
    name: "",
    // display.rs's design::BACKGROUND_COLOR.
    backgroundColor: "#101014",
    backgroundImageAssetId: "",
    layout: "column",
    distribution: "center",
    // A new template opens on the product's own default content rather than an
    // empty canvas, so the preview shows something the moment the page loads.
    sections: [
        { type: "text", id: "s1", text: "Estimated waiting time", size: DEFAULT_SECTION_SIZE },
        { type: "metric", id: "s2", unit: "min", size: DEFAULT_SECTION_SIZE },
    ],
    fontChoice: "inter",
    fontAssetId: "",
    slotText: "",
    customCss: TEMPLATE_CSS_SCAFFOLD,
    previewWidth: 1920,
    previewHeight: 1080,
};

/** A saved section, in form state — an image's `assetId` becomes "" for the
 *  picker rather than staying null. */
function toSectionValues(section: TemplateSection): SectionValues {
    switch (section.type) {
        case "image":
            return { type: "image", id: section.id, assetId: section.assetId ?? "" };
        case "metric":
            return {
                type: "metric",
                id: section.id,
                unit: section.unit ?? "",
                size: section.size ?? DEFAULT_SECTION_SIZE,
            };
        case "text":
            return { type: "text", id: section.id, text: section.text, size: section.size ?? DEFAULT_SECTION_SIZE };
    }
}

export function toDefaults(template: Template): TemplateValues {
    return {
        name: template.name,
        backgroundColor: template.backgroundColor,
        backgroundImageAssetId: template.backgroundImageAssetId ?? "",
        layout: template.layout,
        distribution: template.distribution,
        sections: template.sections.map(toSectionValues),
        fontChoice: fontChoiceFor(template.fontStack, template.fontAssetId),
        fontAssetId: template.fontAssetId ?? "",
        slotText: template.colorSlots.text ?? "",
        // Empty shows the commented cheat-sheet scaffold; it only persists
        // once actually edited.
        customCss: template.customCss || TEMPLATE_CSS_SCAFFOLD,
        previewWidth: template.previewWidth ?? 1920,
        previewHeight: template.previewHeight ?? 1080,
    };
}

/** The chosen stack. For an uploaded font, the fallback signage shows while
 *  the file loads or if it fails. */
function chosenFontStack(values: TemplateValues): string | null {
    if (values.fontChoice === OWN_FONT) return UPLOADED_FONT_FALLBACK;
    return FONT_STACKS[values.fontChoice] ?? null;
}

/** The uploaded font, only while "Your font" is the choice: picking a bundled
 *  card clears it on the next save even though the form still remembers it. */
function chosenFontAssetId(values: TemplateValues): string | null {
    return values.fontChoice === OWN_FONT ? values.fontAssetId || null : null;
}

function editedCss(values: TemplateValues): string {
    return isPristineCss(values.customCss, TEMPLATE_CSS_SCAFFOLD) ? "" : values.customCss;
}

/** Inverts `toSectionValues` for one section: the wire's null-means-cleared
 *  convention for an emptied asset or unit. `assetUrl` is always sent null
 *  here — the real resolved URL is filled in by `resolveArtworkUrls` at
 *  submit (Task 10). */
function toWireSection(section: SectionValues): TemplateSection {
    switch (section.type) {
        case "image":
            return {
                type: "image",
                id: section.id,
                assetId: section.assetId || null,
                assetUrl: null,
            };
        case "metric":
            return { type: "metric", id: section.id, unit: section.unit.trim() || null, ...wireSize(section.size) };
        case "text":
            return { type: "text", id: section.id, text: section.text, ...wireSize(section.size) };
    }
}

function toWireSections(values: TemplateValues): TemplateSection[] {
    return values.sections.map(toWireSection);
}

export function toBody(values: TemplateValues): TemplateBody {
    const colorSlots: Record<string, string> = {};
    if (values.slotText !== "") colorSlots.text = values.slotText;
    return {
        name: values.name,
        backgroundColor: values.backgroundColor,
        backgroundImageAssetId: values.backgroundImageAssetId || null,
        layout: values.layout,
        distribution: values.distribution,
        sections: toWireSections(values),
        fontStack: chosenFontStack(values),
        fontAssetId: chosenFontAssetId(values),
        colorSlots,
        customCss: editedCss(values) || null,
        previewWidth: values.previewWidth,
        previewHeight: values.previewHeight,
    };
}

/** The in-progress draft the preview iframe renders, straight from the form:
 *  the same `layout` and `sections` the operator is editing, so the preview
 *  follows every keystroke rather than showing frozen placeholder content. */
export function toPreviewTemplate(values: TemplateValues): PreviewTemplate {
    return {
        backgroundColor: HEX.test(values.backgroundColor) ? values.backgroundColor : "#101014",
        backgroundImageAssetId: values.backgroundImageAssetId || null,
        layout: values.layout,
        distribution: values.distribution,
        sections: toWireSections(values),
        fontStack: chosenFontStack(values),
        fontAssetId: chosenFontAssetId(values),
        colorSlots: values.slotText ? { text: values.slotText } : {},
        customCss: editedCss(values) || null,
    };
}
